import { useState } from 'react';

interface ProcessDetail {
  id: string;
  title: string;
  steps: { number: string; name: string; description: string; icon: string; image: string; }[];
  summary: string;
}

const processDetails: ProcessDetail[] = [
  {
    id: 'design',
    title: '1-3단계: 설계 및 가공',
    summary: 'CAD 설계도면 · 레이저/NCT 프로그램 · 가공',
    steps: [
      {
        number: '01',
        name: 'CAD 설계도면',
        icon: 'ri-draft-line',
        image: 'https://public.readdy.ai/ai/img_res/0b4e95ec94a06abef49cf7b64be1274a.jpg',
        description: '고객 요구사항을 바탕으로 AutoCAD, SolidWorks 등 전문 CAD 소프트웨어를 활용하여 정밀 설계도면을 작성합니다. 치수 공차, 재질, 표면처리 등 모든 사양을 도면에 반영합니다.',
      },
      {
        number: '02',
        name: '레이저/NCT 프로그램',
        icon: 'ri-code-s-slash-line',
        image: 'https://readdy.ai/api/search-image?query=CNC%20programming%20screen%20showing%20nesting%20software%20with%20metal%20sheet%20layout%20optimization%2C%20NC%20code%20generation%20for%20laser%20cutting%20machine%2C%20industrial%20computer%20terminal%20with%20technical%20program%20interface%2C%20factory%20control%20room%20environment%20with%20machinery%20in%20background&width=600&height=360&seq=process-nct-program-v1&orientation=landscape',
        description: '설계도면을 기반으로 레이저 커팅 및 NCT 펀칭 가공을 위한 NC 프로그램을 작성합니다. 최적의 네스팅(Nesting)으로 재료 손실을 최소화합니다.',
      },
      {
        number: '03',
        name: '가공',
        icon: 'ri-tools-line',
        image: 'https://readdy.ai/api/search-image?query=high%20power%20fiber%20laser%20cutting%20machine%20processing%20thick%20steel%20metal%20sheet%20with%20intense%20bright%20orange%20sparks%20flying%20in%20dark%20industrial%20factory%2C%20precision%20laser%20beam%20cutting%20through%20metal%20plate%2C%20dramatic%20industrial%20manufacturing%20scene%20with%20glowing%20molten%20metal%20and%20smoke&width=600&height=360&seq=process-laser-cutting-v1&orientation=landscape',
        description: '고출력 파이버 레이저 장비와 NCT 프레스를 이용하여 금속 판재를 정밀하게 절단 및 타공합니다. 최대 ±0.1mm의 가공 정밀도를 보장합니다.',
      },
    ],
  },
  {
    id: 'welding',
    title: '4-5단계: 절곡 및 용접',
    summary: '벤딩 · TAP 가공 · 용접',
    steps: [
      {
        number: '04',
        name: '벤딩 · TAP 가공',
        icon: 'ri-scissors-cut-line',
        image: 'https://readdy.ai/api/search-image?query=Bystronic%20press%20brake%20bending%20machine%20forming%20thick%20steel%20metal%20sheet%20at%20precise%20angle%20in%20industrial%20factory%2C%20metal%20sheet%20bending%20process%20with%20hydraulic%20press%2C%20worker%20operating%20CNC%20bending%20machine%20with%20control%20panel%2C%20professional%20sheet%20metal%20fabrication%20workshop&width=600&height=360&seq=process-bending-v1&orientation=landscape',
        description: '신뢰성있는 BYstronic 프레스 장비를 이용하여 도면에 따라 정확한 각도로 절곡 작업을 수행합니다. 동시에 TAP 가공으로 나사산을 형성하여 조립 편의성을 높입니다.',
      },
      {
        number: '05',
        name: '용접',
        icon: 'ri-fire-line',
        image: 'https://readdy.ai/api/search-image?query=skilled%20welder%20wearing%20protective%20helmet%20and%20gloves%20performing%20TIG%20welding%20on%20stainless%20steel%20metal%20parts%20with%20bright%20arc%20light%20and%20orange%20sparks%2C%20close%20up%20professional%20welding%20process%20in%20industrial%20workshop%2C%20dramatic%20sparks%20and%20glowing%20weld%20pool%20with%20dark%20background&width=600&height=360&seq=process-welding-v1&orientation=landscape',
        description: 'MIG, TIG, CO₂ 레이저 용접 등 다양한 용접 방식을 적용하여 강도 높은 접합을 실현합니다. 숙련된 용접 기술자가 직접 작업하여 용접 품질과 외관을 동시에 확보합니다.',
      },
    ],
  },
  {
    id: 'finishing',
    title: '6-9단계: 후처리 및 납품',
    summary: '도장/도금 · 조립 · 검사/포장 · 납품',
    steps: [
      {
        number: '06',
        name: '도장 / 도금',
        icon: 'ri-paint-brush-line',
        image: 'https://readdy.ai/api/search-image?query=powder%20coating%20process%20spraying%20orange%20paint%20on%20metal%20parts%20in%20industrial%20spray%20booth%2C%20professional%20surface%20treatment%20finishing%20for%20steel%20fabricated%20components%2C%20worker%20in%20protective%20suit%20applying%20electrostatic%20powder%20coating%2C%20clean%20factory%20environment%20with%20ventilation%20system&width=600&height=360&seq=process-coating-v1&orientation=landscape',
        description: '분체도장, 액체도장, 아연도금, 니켈도금 등 고객 요구에 맞게 표면처리를 진행합니다. 내식성과 내구성을 높이고 제품의 외관 품질을 완성합니다.',
      },
      {
        number: '07',
        name: '조립',
        icon: 'ri-settings-3-line',
        image: 'https://readdy.ai/api/search-image?query=worker%20assembling%20precision%20metal%20fabricated%20parts%20and%20components%20on%20industrial%20workbench%2C%20professional%20assembly%20process%20with%20bolts%20nuts%20and%20fasteners%2C%20clean%20manufacturing%20facility%20with%20organized%20tools%20and%20finished%20metal%20products%2C%20quality%20assembly%20line%20environment&width=600&height=360&seq=process-assembly-v1&orientation=landscape',
        description: '각 부품을 도면 사양에 맞게 정확히 조립합니다. 필요 시 볼트, 너트, 인서트 등 체결 부품을 포함하여 완제품 형태로 납품 가능합니다.',
      },
      {
        number: '08',
        name: '검사 / 포장',
        icon: 'ri-checkbox-circle-line',
        image: 'https://public.readdy.ai/ai/img_res/edited_19aef3665b7f200e7dd4b15334730fd1_d5d0761e.jpg',
        description: `모든 제품은 치수, 외관, 기능 검사를 거쳐 품질 기준을 충족하는지 확인합니다. 출하 전 상차 시에도 최종 검사를 진행하며, 파손 방지를 위한 안전 포장을 통해 신뢰할 수 있는 상태로 배송합니다.`,
      },
      {
        number: '09',
        name: '납품',
        icon: 'ri-truck-line',
        image: 'https://readdy.ai/api/search-image?query=logistics%20truck%20loading%20finished%20metal%20fabricated%20products%20at%20industrial%20warehouse%20dock%2C%20professional%20delivery%20and%20shipping%20of%20manufactured%20metal%20parts%2C%20forklift%20loading%20packaged%20metal%20components%20onto%20delivery%20truck%2C%20clean%20industrial%20logistics%20facility%20with%20organized%20storage&width=600&height=360&seq=process-delivery-v1&orientation=landscape',
        description: '지정된 납기일에 맞춰 안전하게 납품합니다. 납품 후에도 품질 이슈 발생 시 신속하게 대응하며, 고객 만족을 최우선으로 합니다.',
      },
    ],
  },
];

export default function ProcessSection() {
  const [selectedProcess, setSelectedProcess] = useState<ProcessDetail | null>(null);

  return (
    <section id="process" className="py-16 md:py-28 px-5 md:px-20 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Production Process</span>
          <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mt-2 leading-tight">
            체계적인 <span className="text-orange-500">9단계</span><br />
            <span className="text-orange-500">생산 공정</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 md:h-[580px]">
          {/* 1-3단계 */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-md h-[220px] md:h-auto cursor-pointer group"
            onClick={() => setSelectedProcess(processDetails[0])}
          >
            <img
              src="https://readdy.ai/api/search-image?query=CAD%20software%20design%20screen%20showing%20technical%20blueprint%20and%20laser%20cutting%20machine%20in%20action%20with%20bright%20blue%20cutting%20beam%20on%20metal%20sheet%2C%20modern%20industrial%20manufacturing%20process%20with%20precision%20equipment%20and%20digital%20workflow%20integration&width=800&height=1200&seq=process-design-001&orientation=portrait"
              alt="설계 및 가공"
              className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors duration-300" />
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">3단계</span>
                <p className="text-white font-semibold text-sm md:text-base">1-3단계: 설계 및 가공</p>
              </div>
              <p className="text-white/75 text-xs">CAD 설계도면 · 레이저/NCT 프로그램 · 가공</p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-orange-400 text-xs font-medium">자세히 보기</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-orange-400 text-xs" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 md:gap-6">
            {/* 4-5단계 */}
            <div
              className="relative rounded-2xl overflow-hidden shadow-md h-[160px] md:flex-1 cursor-pointer group"
              onClick={() => setSelectedProcess(processDetails[1])}
            >
              <img
                src="https://readdy.ai/api/search-image?query=welder%20wearing%20protective%20welding%20helmet%20and%20gloves%20welding%20steel%20metal%20plate%20with%20bright%20orange%20sparks%20flying%2C%20close%20up%20action%20shot%20of%20skilled%20worker%20performing%20arc%20welding%20on%20thick%20iron%20sheet%20in%20industrial%20workshop%2C%20dramatic%20sparks%20and%20glowing%20weld%20pool%2C%20professional%20manufacturing%20environment%20with%20dark%20background%20highlighting%20the%20welding%20process&width=800&height=400&seq=process-welding-worker-002&orientation=landscape"
                alt="절곡 및 용접"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors duration-300" />
              <div className="absolute bottom-3 left-3 right-3 bg-black/70 backdrop-blur-sm rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">2단계</span>
                  <p className="text-white font-semibold text-xs md:text-sm">4-5단계: 절곡 및 용접</p>
                </div>
                <p className="text-white/75 text-xs">벤딩 · TAP 가공 · 용접</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-orange-400 text-xs font-medium">자세히 보기</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-orange-400 text-xs" />
                  </div>
                </div>
              </div>
            </div>

            {/* 6-9단계 */}
            <div
              className="relative rounded-2xl overflow-hidden shadow-md h-[160px] md:flex-1 cursor-pointer group"
              onClick={() => setSelectedProcess(processDetails[2])}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/edited_76121319fa21a8d382ac8371090a3cd9_168f54e1.jpg"
                alt="후처리 및 납품"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors duration-300" />
              <div className="absolute bottom-3 left-3 right-3 bg-black/70 backdrop-blur-sm rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">4단계</span>
                  <p className="text-white font-semibold text-xs md:text-sm">6-9단계: 후처리 및 납품</p>
                </div>
                <p className="text-white/75 text-xs">도장/도금 · 조립 · 검사/포장 · 납품</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-orange-400 text-xs font-medium">자세히 보기</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-orange-400 text-xs" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 팝업 */}
      {selectedProcess && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedProcess(null)}
        >
          <div
            className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-widest mb-1">공정 상세</p>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">{selectedProcess.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{selectedProcess.summary}</p>
              </div>
              <button
                onClick={() => setSelectedProcess(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer ml-4 shrink-0"
              >
                <i className="ri-close-line text-gray-600 text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {selectedProcess.steps.map((step) => (
                <div key={step.number} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                  {/* 공정 이미지 */}
                  <div className="w-full h-44 overflow-hidden relative">
                    <img
                      src={step.image}
                      alt={step.name}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 text-white shadow">
                        <i className={`${step.icon} text-sm`} />
                      </div>
                      <span className="text-white font-bold text-sm drop-shadow">{step.name}</span>
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-2 py-0.5 rounded-full border border-white/30">STEP {step.number}</span>
                    </div>
                  </div>
                  {/* 공정 설명 */}
                  <div className="p-4 bg-gray-50">
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setSelectedProcess(null)}
                className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
